import React, { useState} from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Stack,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Collapse,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CalendarMonth as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Error as ErrorIcon,
  PlayArrow as PlayIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Store as StoreIcon,
  Business as BusinessIcon,
  Info as InfoIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../utils/axiosConfig';
import { Plan, listPlans } from '../services/planService';
import { listWorkers, Worker } from '../services/workerService';
import { listStores, Store } from '../services/storeService';
import { listPartners, Partner } from '../services/partnerService';
import { listPrograms, Program} from '../services/programService';

interface OptimizationStatus {
  plan_id: number;
  status: 'not_started' | 'pending' | 'running' | 'completed' | 'failed';
  created_at?: string;
  completed_at?: string;
  error_message?: string;
  metrics?: {
    total_routes: number;
    total_distance: number;
    total_time: number;
    unassigned_visits: number;
    optimization_time: number;
  };
}

interface PlanWithDetails extends Plan {
  optimization_status?: OptimizationStatus;
  included_partners?: Partner[];
  included_workers?: Worker[];
  included_stores?: Store[];
  programs?: Program[];
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

const PlanOptimizationView: React.FC = () => {
  const queryClient = useQueryClient();
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [optimizationFilter, setOptimizationFilter] = useState<string>('all');
  const [expandedPlan, setExpandedPlan] = useState<number | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<PlanWithDetails | null>(null);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // Fetch all necessary data
  const { data: plans = [], isLoading: plansLoading } = useQuery({
    queryKey: ['plans', selectedMonth],
    queryFn: async () => {
      const allPlans = await listPlans();
      if (selectedMonth) {
        return allPlans.filter((p: Plan) => p.month === selectedMonth);
      }
      return allPlans;
    }
  });

  const { data: workers = [] } = useQuery({
    queryKey: ['workers'],
    queryFn: () => listWorkers()
  });

  const { data: stores = [] } = useQuery({
    queryKey: ['stores'],
    queryFn: () => listStores()
  });

  const { data: partners = [] } = useQuery({
    queryKey: ['partners'],
    queryFn: () => listPartners()
  });

  const { data: programs = [] } = useQuery({
    queryKey: ['programs'],
    queryFn: () => listPrograms()
  });

  // Fetch optimization statuses for all plans
  const { data: optimizationStatuses = {} } = useQuery({
    queryKey: ['optimization-statuses', plans],
    queryFn: async () => {
      const statuses: { [key: number]: OptimizationStatus } = {};
      for (const plan of plans) {
        try {
          const response = await axiosInstance.get(`/api/v1/plans/${plan.id}/optimization-status`);
          statuses[plan.id] = response.data;
        } catch {
          statuses[plan.id] = {
            plan_id: plan.id,
            status: 'not_started'
          };
        }
      }
      return statuses;
    },
    enabled: plans.length > 0
  });

  // Run optimization mutation
  const runOptimization = useMutation({
    mutationFn: async (planId: number) => {
      return await axiosInstance.post(`/api/v1/plans/${planId}/optimize-with-ortools`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['optimization-statuses'] });
    }
  });

  // Export optimization results
  const exportResults = async (planId: number, format: 'json' | 'excel') => {
    try {
      const response = await axiosInstance.get(
        `/api/v1/plans/${planId}/export-optimization`,
        {
          params: { format },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `optimization_plan_${planId}.${format === 'json' ? 'json' : 'xlsx'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (_error) {
      console.error('Failed to export results:', _error);
    }
  };

  // Enhanced plans with details
  const enhancedPlans: PlanWithDetails[] = plans.map(plan => {
    // Get workers included in this plan
    const planWorkers = plan.plan_workers?.map(pw => 
      workers.find(w => w.id === pw.worker_id)
    ).filter(Boolean) as Worker[] || [];

    // Get stores from program stores
    const planStores: Store[] = [];
    const planPrograms: Program[] = [];
    const planPartners: Partner[] = [];
    
    // Since we don't have direct access to program stores in the Program interface,
    // we'll use a simplified approach based on available data
    if (plan.plan_program_stores) {
      const partnerIds = new Set<number>();
      
      // For now, we'll just get unique programs from the plan data
      // In a real implementation, you would need to fetch program details separately
      plan.plan_program_stores.forEach(pps => {
        // Extract program ID from the program_store_id if available
        // This is a simplified approach
        if (pps.program_store_id) {
          // We would need additional API calls to get the actual mappings
          // For now, just use available stores
        }
      });
      
      // Get all unique stores from the stores list
      // This is a simplified version
      stores.forEach(store => {
        if (store.id) {
          planStores.push(store);
        }
      });
      
      // Get all programs and partners
      programs.forEach(program => {
        if (program.id) {
          planPrograms.push(program);
          
          // Find the partner
          const partner = partners.find(p => p.id === program.partner_id);
          if (partner && !partnerIds.has(partner.id!)) {
            partnerIds.add(partner.id!);
            planPartners.push(partner);
          }
        }
      });
    }

    return {
      ...plan,
      optimization_status: optimizationStatuses[plan.id],
      included_partners: planPartners,
      included_workers: planWorkers,
      included_stores: planStores,
      programs: planPrograms
    };
  });

  // Filter by optimization status
  const filteredPlans = enhancedPlans.filter(plan => {
    if (optimizationFilter === 'all') return true;
    if (optimizationFilter === 'optimized') {
      return plan.optimization_status?.status === 'completed';
    }
    if (optimizationFilter === 'not_optimized') {
      return !plan.optimization_status || plan.optimization_status.status === 'not_started';
    }
    if (optimizationFilter === 'pending') {
      return plan.optimization_status?.status === 'pending' || plan.optimization_status?.status === 'running';
    }
    return true;
  });

  const getStatusIcon = (status?: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon color="success" />;
      case 'running':
      case 'pending':
        return <PendingIcon color="warning" />;
      case 'failed':
        return <ErrorIcon color="error" />;
      default:
        return <InfoIcon color="disabled" />;
    }
  };

  const getStatusColor = (status?: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'running':
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleViewDetails = (plan: PlanWithDetails) => {
    setSelectedPlan(plan);
    setDetailsDialog(true);
    setActiveTab(0);
  };

  const handleRunOptimization = (planId: number) => {
    if (window.confirm('Start optimization for this plan?')) {
      runOptimization.mutate(planId);
    }
  };

  return (
    <Box>
      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack spacing={3}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
            <Box sx={{ flex: 1 }}>
              <TextField
                fullWidth
                label="Filter by Month"
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            
            <Box sx={{ flex: 1 }}>
              <FormControl fullWidth>
                <InputLabel>Optimization Status</InputLabel>
                <Select
                  value={optimizationFilter}
                  onChange={(e) => setOptimizationFilter(e.target.value)}
                  label="Optimization Status"
                >
                  <MenuItem value="all">All Plans</MenuItem>
                  <MenuItem value="optimized">Optimized</MenuItem>
                  <MenuItem value="not_optimized">Not Optimized</MenuItem>
                  <MenuItem value="pending">In Progress</MenuItem>
                </Select>
              </FormControl>
            </Box>
            
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => queryClient.invalidateQueries()}
              sx={{ minWidth: 120 }}
            >
              Refresh
            </Button>
            
            <Stack direction="row" spacing={2}>
              <Chip
                icon={<AssignmentIcon />}
                label={`Total Plans: ${filteredPlans.length}`}
                color="primary"
              />
              <Chip
                icon={<CheckCircleIcon />}
                label={`Optimized: ${filteredPlans.filter(p => p.optimization_status?.status === 'completed').length}`}
                color="success"
              />
            </Stack>
          </Stack>
        </Stack>
      </Paper>

      {/* Plans List */}
      {plansLoading ? (
        <LinearProgress />
      ) : (
        <Stack spacing={3}>
          {filteredPlans.map((plan) => (
            <Box key={plan.id}>
              <Card>
                <CardContent>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
                    <Box sx={{ flex: '0 0 auto', width: { xs: '100%', md: '25%' } }}>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        {getStatusIcon(plan.optimization_status?.status)}
                        <Box>
                          <Typography variant="h6">{plan.name}</Typography>
                          <Typography variant="body2" color="textSecondary">
                            <CalendarIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                            {plan.month} • {plan.days} days
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                    
                    <Box sx={{ flex: '0 0 auto', width: { xs: '100%', md: '16%' } }}>
                      <Stack spacing={1}>
                        <Chip
                          icon={<BusinessIcon />}
                          label={`${plan.included_partners?.length || 0} Partners`}
                          size="small"
                          color="secondary"
                        />
                        <Chip
                          label={plan.included_partners?.map(p => p.name).join(', ') || 'No partners'}
                          size="small"
                          variant="outlined"
                        />
                      </Stack>
                    </Box>
                    
                    <Box sx={{ flex: '0 0 auto', width: { xs: '100%', md: '16%' } }}>
                      <Stack direction="row" spacing={1}>
                        <Chip
                          icon={<PersonIcon />}
                          label={`${plan.included_workers?.length || 0} Workers`}
                          size="small"
                          color="primary"
                        />
                        <Chip
                          icon={<StoreIcon />}
                          label={`${plan.included_stores?.length || 0} Stores`}
                          size="small"
                          color="info"
                        />
                      </Stack>
                    </Box>
                    
                    <Box sx={{ flex: '0 0 auto', width: { xs: '100%', md: '16%' } }}>
                      <Chip
                        label={plan.optimization_status?.status || 'Not Started'}
                        color={getStatusColor(plan.optimization_status?.status)}
                        sx={{ fontWeight: 'bold' }}
                      />
                      {plan.optimization_status?.completed_at && (
                        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                          {format(new Date(plan.optimization_status.completed_at), 'MMM dd, HH:mm')}
                        </Typography>
                      )}
                    </Box>
                    
                    <Box sx={{ flex: '1 1 auto', minWidth: { xs: '100%', md: 'auto' } }}>
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleViewDetails(plan)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        
                        {plan.optimization_status?.status === 'completed' && (
                          <>
                            <Tooltip title="Download JSON">
                              <IconButton
                                size="small"
                                onClick={() => exportResults(plan.id, 'json')}
                              >
                                <DownloadIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Download Excel">
                              <IconButton
                                size="small"
                                onClick={() => exportResults(plan.id, 'excel')}
                              >
                                <AssignmentIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        
                        {(!plan.optimization_status || plan.optimization_status.status === 'not_started' || plan.optimization_status.status === 'failed') && (
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<PlayIcon />}
                            onClick={() => handleRunOptimization(plan.id)}
                            disabled={runOptimization.isPending}
                          >
                            Optimize
                          </Button>
                        )}
                        
                        <IconButton
                          size="small"
                          onClick={() => setExpandedPlan(expandedPlan === plan.id ? null : plan.id)}
                        >
                          {expandedPlan === plan.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </Stack>
                    </Box>
                  </Stack>
                  
                  {/* Optimization Metrics */}
                  {plan.optimization_status?.metrics && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                      <Stack direction="row" spacing={2} flexWrap="wrap">
                        <Box sx={{ flex: '0 0 auto', minWidth: '120px' }}>
                          <Typography variant="caption" color="textSecondary">Total Routes</Typography>
                          <Typography variant="h6">{plan.optimization_status.metrics.total_routes}</Typography>
                        </Box>
                        <Box sx={{ flex: '0 0 auto', minWidth: '120px' }}>
                          <Typography variant="caption" color="textSecondary">Total Distance</Typography>
                          <Typography variant="h6">{(plan.optimization_status.metrics.total_distance / 1000).toFixed(1)} km</Typography>
                        </Box>
                        <Box sx={{ flex: '0 0 auto', minWidth: '120px' }}>
                          <Typography variant="caption" color="textSecondary">Total Time</Typography>
                          <Typography variant="h6">{(plan.optimization_status.metrics.total_time / 60).toFixed(0)} min</Typography>
                        </Box>
                        <Box sx={{ flex: '0 0 auto', minWidth: '150px' }}>
                          <Typography variant="caption" color="textSecondary">Unassigned Visits</Typography>
                          <Typography variant="h6" color={plan.optimization_status.metrics.unassigned_visits > 0 ? 'error' : 'inherit'}>
                            {plan.optimization_status.metrics.unassigned_visits}
                          </Typography>
                        </Box>
                        <Box sx={{ flex: '0 0 auto', minWidth: '150px' }}>
                          <Typography variant="caption" color="textSecondary">Optimization Time</Typography>
                          <Typography variant="h6">{plan.optimization_status.metrics.optimization_time.toFixed(2)}s</Typography>
                        </Box>
                      </Stack>
                    </Box>
                  )}
                  
                  {/* Expanded Details */}
                  <Collapse in={expandedPlan === plan.id}>
                    <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            <BusinessIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                            Partners ({plan.included_partners?.length || 0})
                          </Typography>
                          <List dense>
                            {plan.included_partners?.map(partner => (
                              <ListItem key={partner.id}>
                                <ListItemText primary={partner.name} />
                              </ListItem>
                            )) || <ListItem><ListItemText primary="No partners" /></ListItem>}
                          </List>
                        </Box>
                        
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            <PersonIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                            Workers ({plan.included_workers?.length || 0})
                          </Typography>
                          <List dense>
                            {plan.included_workers?.slice(0, 5).map(worker => (
                              <ListItem key={worker.id}>
                                <ListItemText 
                                  primary={worker.name}
                                  secondary={worker.transport_type}
                                />
                              </ListItem>
                            ))}
                            {(plan.included_workers?.length || 0) > 5 && (
                              <ListItem>
                                <ListItemText 
                                  primary={`+${plan.included_workers!.length - 5} more`}
                                />
                              </ListItem>
                            )}
                          </List>
                        </Box>
                        
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            <StoreIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                            Stores ({plan.included_stores?.length || 0})
                          </Typography>
                          <List dense>
                            {plan.included_stores?.slice(0, 5).map(store => (
                              <ListItem key={store.id}>
                                <ListItemText 
                                  primary={store.address}
                                />
                              </ListItem>
                            ))}
                            {(plan.included_stores?.length || 0) > 5 && (
                              <ListItem>
                                <ListItemText 
                                  primary={`+${plan.included_stores!.length - 5} more`}
                                />
                              </ListItem>
                            )}
                          </List>
                        </Box>
                      </Stack>
                    </Box>
                  </Collapse>
                </CardContent>
              </Card>
            </Box>
          ))}
        </Stack>
      )}

      {/* Details Dialog */}
      <Dialog
        open={detailsDialog}
        onClose={() => setDetailsDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {selectedPlan?.name} - Detailed View
        </DialogTitle>
        <DialogContent>
          {selectedPlan && (
            <>
              <Tabs value={activeTab} onChange={(_e, v) => setActiveTab(v)}>
                <Tab label="Partners" icon={<BusinessIcon />} />
                <Tab label="Workers" icon={<PersonIcon />} />
                <Tab label="Stores" icon={<StoreIcon />} />
                <Tab label="Programs" icon={<AssignmentIcon />} />
              </Tabs>
              
              <TabPanel value={activeTab} index={0}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Partner Name</TableCell>
                        <TableCell>Programs</TableCell>
                        <TableCell>Stores</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedPlan.included_partners?.map(partner => (
                        <TableRow key={partner.id}>
                          <TableCell>{partner.name}</TableCell>
                          <TableCell>
                            {selectedPlan.programs?.filter(p => p.partner_id === partner.id).length || 0}
                          </TableCell>
                          <TableCell>
                            {selectedPlan.included_stores?.filter(_s =>
                              selectedPlan.programs?.some(p => 
                                p.partner_id === partner.id
                              )
                            ).length || 0}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </TabPanel>
              
              <TabPanel value={activeTab} index={1}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Worker Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Transport</TableCell>
                        <TableCell>Starting Location</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedPlan.included_workers?.map(worker => (
                        <TableRow key={worker.id}>
                          <TableCell>{worker.name}</TableCell>
                          <TableCell>{worker.email}</TableCell>
                          <TableCell>
                            <Chip label={worker.transport_type} size="small" />
                          </TableCell>
                          <TableCell>{worker.starting_location}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </TabPanel>
              
              <TabPanel value={activeTab} index={2}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Store Address</TableCell>
                        <TableCell>Chain</TableCell>
                        <TableCell>Programs</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedPlan.included_stores?.map(store => (
                        <TableRow key={store.id}>
                          <TableCell>{store.address}</TableCell>
                          <TableCell>{store.chain_name}</TableCell>
                          <TableCell>
                            {selectedPlan.programs?.filter(p => 
                              p.id // Simplified since program_stores is not in interface
                            ).map(p => p.name).join(', ')}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </TabPanel>
              
              <TabPanel value={activeTab} index={3}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Program Name</TableCell>
                        <TableCell>Partner</TableCell>
                        <TableCell>Stores Count</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedPlan.programs?.map(program => (
                        <TableRow key={program.id}>
                          <TableCell>{program.name}</TableCell>
                          <TableCell>
                            {selectedPlan.included_partners?.find(p => p.id === program.partner_id)?.name}
                          </TableCell>
                          <TableCell>
                            {0} {/* program_stores not in interface */}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </TabPanel>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PlanOptimizationView;